/**
 * Checks the ID token claims oidc-client-ts leaves unchecked (it checks `state`, `nonce` and
 * `sub`): the checks auth0-spa-js made. Signatures aren't checked here:
 * self-service-api and gandalf validate the tokens they are sent.
 */

/** Allowed clock skew, in seconds; auth0-spa-js's default. */
export const ID_TOKEN_LEEWAY_SECONDS = 60;

export interface ExpectedIdTokenClaims {
  issuer: string;
  clientId: string;
  /** Seconds since the epoch. */
  now?: number;
}

const decodePayload = (jwt: string): Record<string, unknown> => {
  const part = jwt.split(".")[1];
  if (!part) {
    throw new Error("ID token is not a JWT");
  }
  const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
  const json = decodeURIComponent(
    Array.from(atob(base64), (c) => `%${c.charCodeAt(0).toString(16).padStart(2, "0")}`).join("")
  );
  const payload: unknown = JSON.parse(json);
  if (!payload || typeof payload !== "object") {
    throw new Error("ID token payload is not an object");
  }
  return payload as Record<string, unknown>;
};

/**
 * Throws if the ID token wasn't issued by `issuer` for `clientId`, or has expired.
 */
export function validateIdTokenClaims(idToken: string, expected: ExpectedIdTokenClaims): void {
  const claims = decodePayload(idToken);
  const now = expected.now ?? Math.floor(Date.now() / 1000);

  if (claims.iss !== expected.issuer) {
    throw new Error(`ID token issuer (iss) "${String(claims.iss)}" is not "${expected.issuer}"`);
  }
  const aud = Array.isArray(claims.aud) ? claims.aud : [claims.aud];
  if (!aud.includes(expected.clientId)) {
    throw new Error("ID token audience (aud) does not include the client ID");
  }
  // OIDC Core 3.1.3.7: azp is required with several audiences, and must be the client when present.
  if ((aud.length > 1 || claims.azp !== undefined) && claims.azp !== expected.clientId) {
    throw new Error("ID token authorized party (azp) is not the client ID");
  }
  if (typeof claims.exp !== "number") {
    throw new Error("ID token has no expiry (exp)");
  }
  if (now > claims.exp + ID_TOKEN_LEEWAY_SECONDS) {
    throw new Error("ID token has expired");
  }
  if (typeof claims.iat !== "number") {
    throw new Error("ID token has no issued-at time (iat)");
  }
  if (
    claims.nbf !== undefined &&
    (typeof claims.nbf !== "number" || now < claims.nbf - ID_TOKEN_LEEWAY_SECONDS)
  ) {
    throw new Error("ID token is not valid yet (nbf)");
  }
}

/** The `exp` claim of an ID token, or `undefined` when it can't be read. */
export function idTokenExpiry(idToken: string): number | undefined {
  try {
    const { exp } = decodePayload(idToken);
    return typeof exp === "number" ? exp : undefined;
  } catch {
    return undefined;
  }
}

/** The `iss` claim of an ID token, or `undefined` when it can't be read. */
export function idTokenIssuer(idToken: string): string | undefined {
  try {
    const { iss } = decodePayload(idToken);
    return typeof iss === "string" ? iss : undefined;
  } catch {
    return undefined;
  }
}
